import type { Theme } from "@react-navigation/native"

const palette = {
  neutral100: "#FFFFFF",
  neutral150: "#F0F0F0",
  neutral175: "#F2F2F2",
  neutral200: "#F4F2F1",
  neutral225: "#E4E4E4",
  neutral300: "#D7CEC9",
  neutral400: "#B6ACA6",
  neutral500: "#978F8A",
  neutral600: "#564E4A",
  neutral700: "#3C3836",
  neutral750: "#303030",
  neutral775: "#404040",
  neutral800: "#191015",
  neutral850: "#808080",
  neutral900: "#000000",

  primary100: "#D0E9F9", // (lightest)
  primary200: "#A1D3F3",
  primary300: "#72BDEB",
  primary400: "#439CE0", // (base color)
  primary500: "#3177B3",
  primary600: "#245285", // (darkest)

  secondary100: "#DCDDE9",
  secondary200: "#BCC0D6",
  secondary300: "#9196B9",
  secondary400: "#626894",
  secondary500: "#41476E",

  accent100: "#FFEED4",
  accent200: "#FFE1B2",
  accent300: "#FDD495",
  accent400: "#FBC878",
  accent500: "#FFBB50",

  angry100: "#F2D6CD",
  angry500: "#C03403",

  overlay20: "rgba(25, 16, 21, 0.2)",
  overlay50: "rgba(25, 16, 21, 0.5)",
  overlay80: "rgba(25, 16, 21, 0.8)",

  shadow: "#3C3836",

  // Primary Colors:
  skText: "#000000",
  skTextDim: "#818181",
  skBackground: "#ffffff",
  skFooterBackground: "#f2f2f2",

  // List Items
  skListItemCategoryBackground: "#e4e4e4",
  skListItemBackground: "#eaeaea",
  skListItemAccent: "#414141",

  skAccessoryBackground: "#f2f2f2",
} as const

export const colorsLight = {
  /**
   * The palette is available to use, but prefer using the name.
   * This is only included for rare, one-off cases. Try to use
   * semantic names as much as possible.
   */
  palette,
  primary: palette.primary400,
  /**
   * A helper for making something see-thru.
   */
  transparent: "rgba(0, 0, 0, 0)",
  /**
   * The default text color in many components.
   */
  text: palette.skText,
  /**
   * Secondary text information.
   */
  textDim: palette.skTextDim,
  /**
   * The default color of the screen background.
   */
  background: palette.skBackground,
  /**
   * The default border color.
   */
  border: palette.neutral850,
  /**
   * The main tinting color.
   */
  tint: palette.primary500,
  /**
   * A subtle color used for lines.
   */
  separator: palette.neutral300,
  /**
   * The inactive tinting color.
   */
  tintInactive: palette.skFooterBackground,
  /**
   * Error messages.
   */
  error: palette.angry500,
  /**
   * Error Background.
   */
  errorBackground: palette.angry100,
  /**
   * The Color of Shadows.
   * This is a special color that is used to create shadows in the app.
   */
  shadow: palette.shadow,

  white: "#FFFFFF",
  buttonBackground: palette.neutral225,
  buttonText: "#414141",
  menuButtonText: "#000000",
  buttonDisabledBackground: palette.neutral300,

  // Button variant backgrounds (normal state)
  buttonDefaultBackground: palette.neutral200, // off-white fill, visible on light bg
  buttonDefaultBorder: palette.neutral300, // subtle border
  buttonFilledBackground: palette.neutral400, // medium gray fill
  buttonReversedBackground: palette.neutral800, // near-black (inverted from light bg)
  buttonReversedText: palette.neutral100, // white text on dark reversed bg

  // Button variant backgrounds (pressed state)
  buttonDefaultPressedBackground: palette.neutral300,
  buttonFilledPressedBackground: palette.neutral500,
  buttonReversedPressedBackground: palette.neutral700,
  inputBackground: palette.neutral175,
  inputText: palette.skText,
  inputBorder: palette.neutral175,
  userBubbleBackground: palette.neutral150,
  contentCardTitleBackground: palette.neutral225,
  contentCardTopicBackground: palette.neutral150,
  userBubbleText: palette.skText,
  sidekickBubbleBackground: palette.skBackground,
  sidekickBubbleText: palette.skText,
  avatarBackground: palette.neutral175,
  avatarBorder: palette.neutral900,
  activeAvatarBackground: palette.neutral175,
  activeAvatarBorder: palette.neutral100,
  avatarCaptionBackground: palette.neutral175,
  avatarCaptionText: palette.neutral100,
  captionTextShadow: "transparent",
  avatarControlButtonBackground: palette.neutral225,
  avatarControlButtonIcon: palette.skListItemAccent,
  pillUnselected: palette.neutral225,
  pillSelected: "#909090",
  sectionSeparator: palette.neutral850,
  cardBorder: palette.neutral225,
  mskProfileBackground: palette.neutral175,
  mskProfileBorder: palette.neutral100,
  thinkingDot: palette.skText,
} as const

export const ExpoRouterLightTheme: Theme = {
  dark: false,
  colors: {
    primary: colorsLight.palette.primary400,
    background: colorsLight.background,
    card: colorsLight.white,
    text: colorsLight.text,
    border: colorsLight.border,
    notification: colorsLight.error,
  },
  fonts: {
    // Using the same font configuration as light theme
    regular: {
      fontFamily: "figtreeRegular",
      fontWeight: "normal",
    },
    medium: {
      fontFamily: "figtreeMedium",
      fontWeight: "normal",
    },
    bold: {
      fontFamily: "figtreeSemiBold",
      fontWeight: "normal",
    },
    heavy: {
      fontFamily: "figtreeBold",
      fontWeight: "normal",
    },
  },
}

//  prettier-ignore
const hexAlphaSuffixes = ['00', '03', '05', '08', '0A', '0D', '0F', '12', '14', '17', '1A', '1C', '1F', '21', '24', '26', '29', '2B', '2E', '30', '33', '36', '38', '3B', '3D', '40', '42', '45', '47', '4A', '4D', '4F', '52', '54', '57', '59', '5C', '5E', '61', '63', '66', '69', '6B', '6E', '70', '73', '75', '78', '7A', '7D', '80', '82', '85', '87', '8A', '8C', '8F', '91', '94', '96', '99', '9C', '9E', 'A1', 'A3', 'A6', 'A8', 'AB', 'AD', 'B0', 'B3', 'B5', 'B8', 'BA', 'BD', 'BF', 'C2', 'C4', 'C7', 'C9', 'CC', 'CF', 'D1', 'D4', 'D6', 'D9', 'DB', 'DE', 'E0', 'E3', 'E6', 'E8', 'EB', 'ED', 'F0', 'F2', 'F5', 'F7', 'FA', 'FC', 'FF'] as const

export function addHexAlpha(hexColor: string, opacityPercentage: number) {
  const opacityPercentageNormalized = Math.min(Math.max(Math.round(opacityPercentage), 0), 100)
  if (!/^#([0-9A-F]{3}){1,2}$/i.test(hexColor)) return hexColor

  return `${hexColor}${hexAlphaSuffixes[opacityPercentageNormalized]}`
}
